import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Snackbar,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    QuestionTypeFrontend,
    SessionQuestionEntityFrontend,
} from '../../api/sessionQuestionsApi';
import { addProfessionForPublicShare, translateSessionShareQuestions } from '../../api/publicSessionApi';
import ListItemEditModal from '../Worker/ListItemEditModal';
import { FormSection } from './sections/FormSection';
import { SubmitFooter } from './sections/SubmitFooter';
import { UploadFirstStep } from './sections/UploadFirstStep';
import { SuggestionReviewBanner } from './sections/SuggestionReviewBanner';
import { LanguagePicker } from './sections/LanguagePicker';
import { usePublicShareSession } from './hooks/usePublicShareSession';
import { useSessionFormPersistence } from './hooks/useSessionFormPersistence';
import { usePublicShareUploads } from './hooks/usePublicShareUploads';
import { usePublicSharePrefill } from './hooks/usePublicSharePrefill';
import { useSessionFormI18n } from './hooks/useSessionFormI18n';
import { Language } from '../../models/session';
import { EditableSessionQuestion, questionTypeOrder, SnackbarState } from './utils';

const PublicSessionFormPage: React.FC = () => {
    const { publicShareId } = useParams<{ publicShareId: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { loading, error, sessionSummary, worker, questions, reload, injectWrapped } =
        usePublicShareSession(publicShareId);

    // True while the picker change has fired `/translate-questions` but the
    // response hasn't returned yet. Drives a placeholder over the question
    // section so the worker doesn't see EN questions flash before the
    // translated ones land. Cleared when the response arrives (success or
    // failure — on failure we just show the EN questions).
    const [translatingQuestions, setTranslatingQuestions] = useState<boolean>(false);

    // sessionSummary.language drives every translation lookup. When the worker
    // picks a new language via the LANGUAGE_SELECTION question, the S7 backend
    // fix updates sessions.language, the silent reload after persist re-fetches
    // the summary, and this hook's `t()` switches dictionaries — every child
    // re-renders in the new language without a manual reload.
    const { t } = useSessionFormI18n(sessionSummary?.language);

    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });
    const showSnackbar = useCallback(
        (message: string, severity: SnackbarState['severity']) => {
            setSnackbar({ open: true, message, severity });
        },
        [],
    );
    const closeSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const onAfterPersistRefresh = useCallback(
        async () => {
            await reload({ silent: true });
        },
        [reload],
    );

    const persistence = useSessionFormPersistence({
        publicShareId,
        questions,
        worker,
        onAfterPersistRefresh,
        showSnackbar,
        t,
    });

    const uploadsState = usePublicShareUploads(publicShareId);
    const prefillState = usePublicSharePrefill(publicShareId);

    // The upload widget is ALWAYS rendered now — workers need to be able to
    // add VCA / ID / CERT documents after CV prefill, not just before. What
    // changes is the layout: "hero" mode (big heading, prefill CTAs) for the
    // first interaction; "compact" mode (slim heading, no CTAs) once the
    // worker has either run AI prefill OR dismissed the hero. The file
    // picker + uploaded-files list stay visible in both modes.
    const [uploadStepDismissed, setUploadStepDismissed] = useState<boolean>(false);
    const uploadCompact =
        uploadStepDismissed || prefillState.status !== 'idle';

    const skipUploadStep = () => setUploadStepDismissed(true);

    const onParseWithAi = async () => {
        await prefillState.runPrefill();
    };

    const onApplyPrefill = async () => {
        const result = await prefillState.applyAccepted();
        // Refresh the question list so the cv_prefilled chips render on the
        // appropriate rows and the persistence layer picks up the new values.
        await reload({ silent: true });
        if (result && result.applied > 0) {
            const key = result.applied === 1
                ? 'snackbar_prefill_applied'
                : 'snackbar_prefill_applied_plural';
            showSnackbar(t(key, { count: result.applied }), 'success');
        }
    };

    const onDismissSuggestions = () => {
        prefillState.reset();
        setUploadStepDismissed(true);
    };

    // The LANGUAGE_SELECTION question is hoisted into the welcome card's
    // top-bar picker, so it must NOT also render inside the question form
    // below — otherwise the worker sees the same control twice. We split it
    // out here once and feed the rest of the questions into the normal
    // grouped renderer.
    const languageQuestion = useMemo(
        () => questions.find((q) => q.questionType === QuestionTypeFrontend.LANGUAGE_SELECTION) ?? null,
        [questions],
    );

    // Profession options are owned by the PROFESSION_SELECTION question. The
    // FINAL_PROFESSION_CONFIRMATION "Other professions?" composite reuses
    // them for its conditional dropdown — its own question definition has no
    // `options`, so we lift the list here and forward it through FormSection
    // → QuestionField.
    //
    // Filter: we excise professions the worker already has on their
    // `worker_professions` profile so the dropdown only offers genuinely-new
    // choices. The backend's add endpoint is idempotent on dupes (no-op +
    // 200) but offering them in the picker leads to no-visible-change picks
    // which feel broken to the worker.
    const professionOptions = useMemo(() => {
        const professionQ = questions.find(
            (q) => q.questionType === QuestionTypeFrontend.PROFESSION_SELECTION,
        );
        const allOptions = (professionQ?.options ?? []).map((o) => ({
            key: o.key,
            label: o.label,
        }));
        const activeKeys = new Set(
            (worker?.professions ?? [])
                .filter((p) => p.isActive)
                .map((p) => p.professionKey.toUpperCase()),
        );
        // Also exclude the worker's primary `professionKey` for safety —
        // some older sessions may not have a worker_professions row for it.
        if (worker?.professionKey) {
            activeKeys.add(worker.professionKey.toUpperCase());
        }
        return allOptions.filter((opt) => !activeKeys.has(opt.key.toUpperCase()));
    }, [questions, worker]);

    // True while a `/professions/add` request is in flight. Disables the
    // FINAL_PROFESSION_CONFIRMATION picker + radio so rapid clicks don't
    // fire multiple concurrent adds.
    const [addingProfession, setAddingProfession] = useState<boolean>(false);

    const onAddProfession = useCallback(
        async (professionKey: string) => {
            if (!publicShareId) {
                showSnackbar(t('snackbar_share_missing'), 'error');
                return;
            }
            setAddingProfession(true);
            try {
                const refreshed = await addProfessionForPublicShare(publicShareId, professionKey);
                injectWrapped(refreshed);
            } catch (err: any) {
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    t('snackbar_save_failed');
                showSnackbar(message, 'error');
            } finally {
                setAddingProfession(false);
            }
        },
        [publicShareId, injectWrapped, showSnackbar, t],
    );

    const groupedQuestions = useMemo(() => {
        const groups: Record<QuestionTypeFrontend, SessionQuestionEntityFrontend[]> = {} as Record<
            QuestionTypeFrontend,
            SessionQuestionEntityFrontend[]
        >;
        questions.forEach((question) => {
            // LANGUAGE_SELECTION is hoisted to the welcome-card picker.
            // CV_UPLOAD is covered by the dedicated UploadFirstStep at the
            // top of the page — rendering it again as a "please upload your
            // CV" form row at the bottom is confusing and redundant.
            if (
                question.questionType === QuestionTypeFrontend.LANGUAGE_SELECTION ||
                question.questionType === QuestionTypeFrontend.CV_UPLOAD
            ) {
                return;
            }
            const key = question.questionType;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(question);
        });
        // Stable ordering within each group — questionNumber when present,
        // falls back to questionId so seeded questions stay deterministic.
        Object.values(groups).forEach((list) => {
            list.sort((a, b) => {
                const aNumber = (a as any).questionNumber ?? a.questionId;
                const bNumber = (b as any).questionNumber ?? b.questionId;
                return aNumber - bNumber;
            });
        });
        return groups;
    }, [questions]);

    const onLanguageChange = useCallback(
        async (next: Language) => {
            if (!languageQuestion || !publicShareId) return;
            // Two-step flow:
            //   1. persistImmediate writes the LANGUAGE_SELECTION answer +
            //      sessions.language, triggers the auto-refresh of summary +
            //      questions. Chrome flips fast — backend is just a DB write.
            //   2. The question content is still in EN at this point (no
            //      cached translation yet). Mask the question section with a
            //      "translating" overlay, call the dedicated translate
            //      endpoint, swap in the response when it lands.
            //
            // Two-step (not one) because the chrome flip needs sessions.language
            // updated, which only happens after step 1. Combining would force
            // workers to stare at EN chrome for 3-4s while the question
            // translation churns.
            await persistence.persistImmediate(languageQuestion, next);

            if (next === Language.EN) {
                // No translation needed — EN is canonical.
                return;
            }

            setTranslatingQuestions(true);
            try {
                const translated = await translateSessionShareQuestions(publicShareId);
                injectWrapped(translated);
            } catch (err) {
                // Falls back to EN questions visually — the merge layer on
                // the backend already serves EN when no translation cache
                // exists. Logged so we can spot Gemini outages.
                console.warn('Translation of session questions failed; showing EN fallback.', err);
            } finally {
                setTranslatingQuestions(false);
            }
        },
        [languageQuestion, publicShareId, persistence, injectWrapped],
    );

    const [listModalOpen, setListModalOpen] = useState<boolean>(false);
    const [listModalQuestion, setListModalQuestion] =
        useState<EditableSessionQuestion | null>(null);

    const openListModal = useCallback(
        (question: SessionQuestionEntityFrontend) => {
            if (!question.listItemSchema || question.listItemSchema.length === 0) {
                showSnackbar(t('snackbar_list_object_unavailable'), 'info');
                return;
            }
            const currentValue = persistence.formValues[question.id];
            setListModalQuestion({ ...question, answer: currentValue });
            setListModalOpen(true);
        },
        [persistence.formValues, showSnackbar, t],
    );

    const closeListModal = useCallback(() => {
        setListModalOpen(false);
        setListModalQuestion(null);
    }, []);

    const handleListModalSave = useCallback(
        (updatedAnswerJson: string) => {
            if (!listModalQuestion) return;
            void persistence.persistListObject(listModalQuestion, updatedAnswerJson);
            closeListModal();
        },
        [closeListModal, listModalQuestion, persistence],
    );

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size={64} />
            </Box>
        );
    }

    if (persistence.terminalOutcome) {
        const consentQuestion = questions.find(
            (question) => question.questionKey === 'ACTION_CONSENT',
        );
        const closingCopy =
            persistence.terminalOutcome === 'GRANTED'
                ? consentQuestion?.consentAcceptedCopy
                : consentQuestion?.consentDeclinedCopy;
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Container maxWidth="sm">
                    <Paper sx={{ p: { xs: 3, md: 4 } }}>
                        <Alert
                            severity={
                                persistence.terminalOutcome === 'GRANTED'
                                    ? 'success'
                                    : 'info'
                            }
                        >
                            {closingCopy ?? t('snackbar_thanks_saved')}
                        </Alert>
                    </Paper>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Container maxWidth="sm">
                    <Paper sx={{ p: 4 }}>
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            {t('share_link_expired')}
                        </Typography>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `linear-gradient(150deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                py: { xs: 4, md: 8 },
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={8}
                    sx={{
                        p: { xs: 3, md: 5 },
                        mb: 4,
                        borderRadius: 4,
                        backgroundColor: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, transparent 70%)`,
                            pointerEvents: 'none',
                        },
                    }}
                >
                    {languageQuestion && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: { xs: 12, md: 20 },
                                right: { xs: 12, md: 20 },
                                zIndex: 2,
                            }}
                        >
                            <LanguagePicker
                                currentLanguage={sessionSummary?.language ?? Language.EN}
                                onChange={onLanguageChange}
                                disabled={persistence.autoSaving || translatingQuestions}
                            />
                        </Box>
                    )}
                    <Stack
                        spacing={isMobile ? 3 : 4}
                        alignItems={isMobile ? 'flex-start' : 'center'}
                    >
                        <Box
                            component="img"
                            src="/optima-uitzendburo.svg"
                            alt="Optima logo"
                            sx={{ width: isMobile ? 160 : 200, zIndex: 1 }}
                        />
                        <Box sx={{ textAlign: isMobile ? 'left' : 'center', zIndex: 1 }}>
                            <Typography
                                variant={isMobile ? 'h4' : 'h3'}
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    mb: 1,
                                }}
                            >
                                {t('welcome_fallback')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('welcome_subtitle')}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {translatingQuestions ? (
                    // While the translate-questions call is in flight, hide
                    // EVERYTHING below the welcome card and show a single
                    // centered spinner. The earlier overlay-on-questions-only
                    // version let UploadFirstStep + a partial FormSection
                    // header bleed through (which looked tacky and confusing
                    // about which content was about to change).
                    <Paper
                        elevation={1}
                        sx={{
                            py: 8,
                            borderRadius: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            minHeight: 320,
                            backgroundColor: '#fff',
                        }}
                    >
                        <CircularProgress size={44} />
                        <Typography variant="body1" color="text.secondary">
                            {t('translating_questions')}
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <UploadFirstStep
                            uploads={uploadsState.uploads}
                            isAnyUploading={uploadsState.isAnyUploading}
                            onUpload={uploadsState.addAndUpload}
                            onContinueWithoutUploads={skipUploadStep}
                            onParseWithAi={onParseWithAi}
                            parsing={false}
                            t={t}
                            compact={uploadCompact}
                        />

                        <SuggestionReviewBanner
                            status={prefillState.status}
                            suggestions={prefillState.suggestions}
                            accepted={prefillState.accepted}
                            errors={prefillState.errors}
                            processedBy={prefillState.processedBy}
                            onToggle={prefillState.toggleAccepted}
                            onAcceptAll={prefillState.acceptAll}
                            onRejectAll={prefillState.rejectAll}
                            onApply={onApplyPrefill}
                            onDismiss={onDismissSuggestions}
                            t={t}
                        />

                        <Stack spacing={4}>
                            {questionTypeOrder.map((questionType) => (
                                <FormSection
                                    key={questionType}
                                    questionType={questionType}
                                    questions={groupedQuestions[questionType] ?? []}
                                    values={persistence.formValues}
                                    onValueChange={persistence.setValue}
                                    onBlur={persistence.persistFieldOnBlur}
                                    onImmediateChange={persistence.persistImmediate}
                                    onOpenListModal={openListModal}
                                    uploads={uploadsState.uploads}
                                    onUpload={uploadsState.addAndUpload}
                                    professionOptions={professionOptions}
                                    onAddProfession={onAddProfession}
                                    isAddingProfession={addingProfession}
                                    t={t}
                                />
                            ))}
                        </Stack>

                        <Divider sx={{ my: 4 }} />

                        <SubmitFooter
                            submitting={persistence.submitting}
                            autoSaving={persistence.autoSaving}
                            dirty={persistence.dirty}
                            onReset={persistence.resetToServerValues}
                            onSubmit={() => void persistence.submitAll()}
                            t={t}
                        />
                    </>
                )}
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {listModalQuestion && listModalOpen && (
                <ListItemEditModal
                    open={listModalOpen}
                    onClose={closeListModal}
                    question={listModalQuestion}
                    onSave={handleListModalSave}
                />
            )}
        </Box>
    );
};

export default PublicSessionFormPage;
